import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { FormInputCheckbox, FormInputText } from 'feature/formComponents'
import { FormEvent, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, TextField } from 'ui'
import { LoaderInCentalPosition } from 'widget/LoaderInCentalPosition/LoaderInCentalPosition'

import { AddressPopup } from 'feature/addresses/components/AddressPopup/AddressPopup'
import { ContactDataFormValues } from 'feature/company/types/CompanyRegistrationForm'
import styles from '../CompanyRegistration.module.scss'

import { EMAIL_REGEX } from 'constants/validation'

type ContactDataStepProps = {
  onNext: () => void
  isLoading?: boolean
}

/**
 * Шаг 1: Контактные данные
 * ФИО контактного лица, адрес, согласия на обработку ПД
 */
export function ContactDataStep({ onNext, isLoading }: ContactDataStepProps) {
  const [viewMode, setViewMode] = useState<string>('form')
  const companyPhone = sessionStorage.getItem('companyRegistrationPhone') || ''

  const { watch } = useFormContext<ContactDataFormValues>()

  const emailValue = watch('email')
  const firstName = watch('firstName')
  const lastName = watch('lastName')
  const agreeProcessingPersonalData = watch('agreeProcessingPersonalData')
  const isEmailValid = EMAIL_REGEX.test(emailValue.trim())
  const hasForbiddenComTld = (checkedEmail: string): boolean =>
    checkedEmail.trim().toLowerCase().endsWith('.com')

  useEffect(() => {
    if (emailValue?.trim()) {
      sessionStorage.setItem('companyRegistrationEmail', emailValue.trim())
    }
  }, [emailValue])

  const isStepValid = !!(
    emailValue?.trim() &&
    isEmailValid &&
    hasForbiddenComTld(emailValue) === false &&
    firstName?.trim() &&
    lastName?.trim() &&
    agreeProcessingPersonalData
  )

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isStepValid) {
      onNext()
    }
  }

  if (viewMode === 'popup') {
    return <AddressPopup setViewMode={setViewMode} />
  }

  return (
    <div className={styles.companyRegistration__stepWrapper}>
      <form
        className={styles.companyRegistration__form}
        noValidate
        onSubmit={handleSubmit}
        data-testid='company-reg-contact-form'
      >
        <div className={styles.companyRegistration__formHeader}>
          <h1 className='h1'>Контактные данные</h1>
          <p className={styles.companyRegistration__stepDescription}>
            Укажите контактные данные представителя компании
          </p>
        </div>

        <div className={styles.companyRegistration__fieldsWrapper}>
          <FormInputText
            name='email'
            rules={{
              required: 'Email обязателен для заполнения',
              pattern: {
                value: EMAIL_REGEX,
                message: 'Введите корректный email',
              },
            }}
            inputProps={{
              label: 'Email',
              type: 'email',
              required: true,
              inputProps: { maxLength: 128 },
              'data-testid': 'company-reg-email',
            }}
          />
          {companyPhone && (
            <TextField
              value={companyPhone}
              label='Телефон'
              disabled
              data-testid='company-reg-phone'
            />
          )}
          <FormInputText
            name='lastName'
            inputProps={{
              label: 'Фамилия',
              required: true,
              inputProps: { maxLength: 100 },
              'data-testid': 'company-reg-last-name',
            }}
          />
          <FormInputText
            name='firstName'
            inputProps={{
              label: 'Имя',
              required: true,
              inputProps: { maxLength: 100 },
              'data-testid': 'company-reg-first-name',
            }}
          />
          <FormInputText
            name='fatherName'
            inputProps={{
              label: 'Отчество',
              inputProps: { maxLength: 100 },
              'data-testid': 'company-reg-father-name',
            }}
          />
        </div>

        <div className={styles.companyRegistration__checkboxes}>
          <FormInputCheckbox
            name='agreeProcessingPersonalData'
            label={
              <>
                Я согласен на{' '}
                <a href='/privacy-policy' target='_blank' rel='noreferrer'>
                  обработку персональных данных
                </a>
              </>
            }
            checkboxProps={{ 'data-testid': 'company-reg-agree-personal-data' }}
          />
          <FormInputCheckbox
            name='agreeSubscribeAdvertising'
            label='Я согласен на получение рекламных и информационных материалов'
            checkboxProps={{ 'data-testid': 'company-reg-agree-advertising' }}
          />
        </div>

        <Button
          type='submit'
          endIcon={<ChevronRightIcon />}
          disabled={!isStepValid || isLoading}
          size='medium'
          variant='contained'
          className={styles.companyRegistration__submitButton}
          data-testid='company-reg-next-button'
        >
          {isLoading && (
            <LoaderInCentalPosition
              className={styles.companyRegistration__loader}
            />
          )}
          Далее
        </Button>
      </form>
    </div>
  )
}
